const STORAGE_KEY = 'payhook:setupWizard'
const POLL_INTERVAL_MS = 5000
const POLL_EVENT = 'button_load'

function readState (accountId) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultState()
    const parsed = JSON.parse(raw)
    if (parsed && parsed.accountId === accountId) return { ...defaultState(), ...parsed }
  } catch {}
  return defaultState()
}

function defaultState () {
  return {
    accountId: null,
    snippetCopied: false,
    installConfirmed: false,
    detected: false,
    dismissed: false
  }
}

function persist (state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

export function mountSetupWizard ({ container, accountId, stripeConnected, apiRequest, getTestMode, onNavigate }) {
  if (!container || !accountId) return

  const state = readState(accountId)
  state.accountId = accountId

  let pollTimer = null
  let expanded = false

  const render = () => {
    const steps = buildSteps({ state, stripeConnected })
    const allDone = steps.every((s) => s.done)

    if ((allDone || state.dismissed) && !expanded) {
      container.innerHTML = `
        <div class="setup-wizard setup-wizard--collapsed">
          <span class="setup-wizard__collapsed-icon" aria-hidden="true">✓</span>
          <span class="setup-wizard__collapsed-text">Setup complete</span>
          <button type="button" class="btn btn--ghost btn--sm" data-wizard-action="reopen">Show steps</button>
        </div>
      `
      bindActions()
      stopPolling()
      return
    }

    const activeIndex = steps.findIndex((s) => !s.done)

    container.innerHTML = `
      <section class="setup-wizard" aria-label="Payhook setup">
        <header class="setup-wizard__header">
          <div>
            <p class="setup-wizard__eyebrow">Setup</p>
            <h2 class="setup-wizard__title">Get paid access live</h2>
          </div>
          <button type="button" class="setup-wizard__dismiss btn btn--ghost btn--sm" data-wizard-action="dismiss" aria-label="Dismiss setup">Skip for now</button>
        </header>

        <ol class="setup-wizard__steps" role="list">
          ${steps.map((step, i) => renderStep(step, i, activeIndex)).join('')}
        </ol>
      </section>
    `

    bindActions()

    if (steps[2] && !steps[2].done && activeIndex === 2) {
      startPolling()
    } else {
      stopPolling()
    }
  }

  const bindActions = () => {
    container.querySelectorAll('[data-wizard-action]').forEach((el) => {
      el.addEventListener('click', (e) => {
        const action = e.currentTarget.dataset.wizardAction
        if (action === 'dismiss') {
          state.dismissed = true
          expanded = false
          persist(state)
          render()
        } else if (action === 'reopen') {
          state.dismissed = false
          expanded = true
          persist(state)
          render()
        } else if (action === 'confirm-install') {
          state.installConfirmed = true
          persist(state)
          render()
        } else if (action === 'goto-button') {
          onNavigate && onNavigate('button')
          state.snippetCopied = true
          persist(state)
          render()
        } else if (action === 'check-now') {
          checkDetection({ force: true })
        }
      })
    })
  }

  const checkDetection = async ({ force = false } = {}) => {
    if (state.detected && !force) return
    try {
      const data = await apiRequest(`/dashboard/track/${POLL_EVENT}?days=7`)
      const total = (data && data.total) || 0
      if (total > 0) {
        state.detected = true
        persist(state)
        render()
      }
    } catch {
      // network blip — keep polling silently
    }
  }

  const startPolling = () => {
    if (pollTimer) return
    checkDetection()
    pollTimer = window.setInterval(checkDetection, POLL_INTERVAL_MS)
  }

  const stopPolling = () => {
    if (pollTimer) {
      window.clearInterval(pollTimer)
      pollTimer = null
    }
  }

  render()
}

function buildSteps ({ state, stripeConnected }) {
  return [
    {
      key: 'stripe',
      title: 'Connect Stripe',
      done: Boolean(stripeConnected),
      body: stripeConnected
        ? '<p class="setup-wizard__copy">Your Stripe account is connected.</p>'
        : '<p class="setup-wizard__copy">Sign in and authorize Stripe so Payhook can run checkout and send payouts to your account.</p>'
    },
    {
      key: 'install',
      title: 'Copy setup into your builder',
      done: state.installConfirmed,
      body: `
        <p class="setup-wizard__copy">Open the setup notes, copy the setup brief, and paste it into your AI builder or site editor. They will add the unlock button for you.</p>
        <div class="setup-wizard__actions">
          <button type="button" class="btn btn--primary btn--sm" data-wizard-action="goto-button">Open setup brief</button>
          <button type="button" class="btn btn--ghost btn--sm" data-wizard-action="confirm-install">I pasted it</button>
        </div>
      `
    },
    {
      key: 'detect',
      title: 'Wait for the first unlock visit',
      done: state.detected,
      body: state.detected
        ? '<p class="setup-wizard__copy">Detected - your unlock button is live.</p>'
        : `
          <p class="setup-wizard__copy">
            <span class="setup-wizard__spinner" aria-hidden="true"></span>
            Listening for the first visit to your unlock button. Open your site or extension after it is updated.
          </p>
          <div class="setup-wizard__actions">
            <button type="button" class="btn btn--ghost btn--sm" data-wizard-action="check-now">Check now</button>
          </div>
        `
    }
  ]
}

function renderStep (step, index, activeIndex) {
  const status = step.done ? 'done' : index === activeIndex ? 'active' : 'pending'
  const marker = step.done ? '✓' : String(index + 1)
  return `
    <li class="setup-wizard__step setup-wizard__step--${status}">
      <span class="setup-wizard__marker" aria-hidden="true">${marker}</span>
      <div class="setup-wizard__step-body">
        <h3 class="setup-wizard__step-title">${step.title}</h3>
        ${status === 'active' || status === 'done' ? step.body : ''}
      </div>
    </li>
  `
}
